/**
 * Shipping rate service - pure functions for shipping logic
 */

import { ShippingRate, RawShippingRate, GuestAddress } from './checkout-types';

/**
 * Provider logo paths — checked by exact match first, then prefix match
 * for CJ carriers whose display names include a suffix (e.g. "YunExpress Standard").
 */
const PROVIDER_LOGOS: Record<string, string> = {
  'USPS': '/images/shippers/usps-logo.png',
  'UPS': '/images/shippers/ups-logo.svg',
  'FEDEX': '/images/shippers/fedex-logo.png',
  'USHIP': '/images/shippers/uShip-logo.webp',
  'YUNEXPRESS': '/images/shippers/yunexpress-logo.png',
  'LUWEI': '/images/shippers/luwei-logo.png',
  '4PX': '/images/shippers/4px-logo.png',
  'CJ': '/images/shippers/cj-logo.svg',
  'CJPACKET': '/images/shippers/cj-logo.svg',
};

/**
 * Get logo path for a shipping provider.
 * Tries exact match first, then checks if the name starts with a known carrier.
 */
export function getProviderLogo(providerName: string): string | null {
  const upper = providerName.toUpperCase();
  if (PROVIDER_LOGOS[upper]) return PROVIDER_LOGOS[upper];
  // Prefix match: "YUNEXPRESS STANDARD" → matches "YUNEXPRESS"
  for (const key of Object.keys(PROVIDER_LOGOS)) {
    if (upper.startsWith(key)) {
      return PROVIDER_LOGOS[key];
    }
  }
  return null;
}

/**
 * Transform raw shipping rate from API to internal format
 */
export function transformShippingRate(rate: RawShippingRate, shipmentId?: string): ShippingRate {
  const providerName = rate.provider.toUpperCase();
  return {
    id: rate.id,
    amount: parseFloat(String(rate.amount)),
    service: rate.servicelevel?.name || rate.provider,
    provider: {
      name: rate.provider,
      image: getProviderLogo(providerName),
    },
    estimatedDays: rate.estimatedDays || 0,
    durationTerms: rate.durationTerms,
    shipmentId,
    serviceToken: rate.servicelevel?.token,
    shipsFrom: (rate as { warehouseCity?: string }).warehouseCity ?? rate.shipsFrom,
    tax: rate.tax,
  };
}

/**
 * Transform array of raw shipping rates
 */
export function transformShippingRates(rates: RawShippingRate[], shipmentId?: string): ShippingRate[] {
  return rates.map(rate => transformShippingRate(rate, shipmentId));
}

/**
 * Create address hash for deduplication
 */
export function createAddressHash(
  listingId: number | string,
  address: { id?: string | number; name?: string; line1?: string; city?: string; state?: string; postal_code?: string; country?: string } | null,
  isGuest: boolean
): string {
  if (!address) return '';

  if (isGuest) {
    // The name is part of the hash because the rate quote creates the Shippo
    // shipment the label is later printed from. Autofill can complete the
    // address before the name field, and without the name here that first
    // nameless quote would be reused for the label.
    return `${listingId}|${address.name || ''}|${address.line1}|${address.city}|${address.state}|${address.postal_code}|${address.country || 'US'}`;
  }
  return `${listingId}|${address.id}`;
}

/**
 * Build shipping rates request body for authenticated users.
 * `variantVid` is CJ-only — included when the buyer has picked a variant
 * on the product page, omitted otherwise.
 */
export function buildAuthenticatedShippingRequest(
  listingId: number | string,
  variantVid?: string
): { listingId: number | string; variantVid?: string } {
  return variantVid ? { listingId, variantVid } : { listingId };
}

/**
 * Build shipping rates request body for guest users.
 * `variantVid` is CJ-only — see `buildAuthenticatedShippingRequest`.
 */
export function buildGuestShippingRequest(
  listingId: number | string,
  address: GuestAddress,
  variantVid?: string
): {
  listingId: number | string;
  buyerAddress: {
    name: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  variantVid?: string;
} {
  const body: {
    listingId: number | string;
    buyerAddress: {
      name: string;
      line1: string;
      line2: string;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    };
    variantVid?: string;
  } = {
    listingId,
    buyerAddress: {
      name: address.name || '',
      line1: address.line1,
      line2: address.line2 || '',
      city: address.city,
      state: address.state,
      postal_code: address.postal_code,
      country: address.country || 'US',
    },
  };
  if (variantVid) body.variantVid = variantVid;
  return body;
}

/**
 * Get the appropriate shipping rates endpoint
 */
export function getShippingEndpoint(isGuest: boolean): string {
  return isGuest ? '/api/checkout/shipping-rates-guest' : '/api/checkout/shipping-rates';
}

/**
 * Check if error is related to missing dimensions
 */
export function isDimensionsError(error: string | undefined): boolean {
  return !!error && /dimensions/i.test(error);
}

/**
 * Find default address from address list
 */
export function findDefaultAddress<T extends { isDefault?: number | string | boolean }>(
  addresses: T[] | null | undefined
): T | null {
  if (!addresses || addresses.length === 0) return null;

  const defaultAddr = addresses.find(
    (a) => a.isDefault === 1 || a.isDefault === '1' || a.isDefault === true
  );

  return defaultAddr || null;
}

/**
 * Get shipping amount from selected provider
 */
export function getSelectedShippingAmount(
  providers: ShippingRate[],
  selectedIndex: number | null | undefined
): number {
  if (selectedIndex === null || selectedIndex === undefined || selectedIndex < 0) {
    return 0;
  }
  return providers[selectedIndex]?.amount || 0;
}

/**
 * Get selected shipping rate
 */
export function getSelectedShippingRate(
  providers: ShippingRate[],
  selectedIndex: number | null | undefined
): ShippingRate | undefined {
  if (selectedIndex === null || selectedIndex === undefined || selectedIndex < 0) {
    return undefined;
  }
  return providers[selectedIndex];
}
